import { FC, useMemo } from 'react';
import { Button, Typography } from '@mui/material';
import { FeaturedVideoOutlined as FeaturedVideoOutlinedIcon } from '@mui/icons-material';
import { DataGridProProps, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';

import ApiService from '../../../../../ApiService';
import Column from '../../../../../components/Column';
import DataGridPro from '../../../../../components/CDGridPro';
import Row from '../../../../../components/Row';

import { Association, AdCreative as DspAdCreative } from '../../../../../types/AmazonAds';
import { CampaignAdGroup } from '../../../../../types/Campaign';
import CampaignMediaType from '../../../../../features/Campaign/CampaignMediaType';
import { useCampaignPageContext } from '../../../../../hooks/Contexts/Pages/CampaignPageContext';

interface CampaignCreativeAction {
    name: string;
    callback: (dspAdCreative: DspAdCreative) => void;
}

type CampaignCreativeDataGridProps = Omit<DataGridProProps, 'columns' | 'rows'> & {
    rows: DspAdCreative[];
    action: CampaignCreativeAction;
};

const NoRowsOverlay: FC = () => {
    return (
        <Column gap={2} justifyContent="center" flex={1} height="100%">
            <Row justifyContent="center">
                <FeaturedVideoOutlinedIcon sx={{ color: 'action.disabledBackground', fontSize: 80 }} />
            </Row>

            <Row justifyContent="center">
                <Typography variant="subtitle2" color="text.secondary">
                    Creative(s) are not available
                </Typography>
            </Row>
        </Column>
    );
};

const CampaignCreativeDataGrid: FC<CampaignCreativeDataGridProps> = ({ rows, action, ...props }) => {
    const { campaign } = useCampaignPageContext();
    const dspAdGroupIds = useMemo((): string[] => {
        return Array.from(
            new Set(
                rows.flatMap(
                    (row: DspAdCreative) => row.dspCreativeAssociations?.map((a: Association) => a.dspAdGroupId) ?? []
                )
            )
        );
    }, [rows]);

    const { data: adGroups = [], isLoading } = useQuery<CampaignAdGroup[]>({
        enabled: !!dspAdGroupIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adCreatives/adGroups', dspAdGroupIds],
        queryFn: async () => {
            const { data } = await ApiService.getCampaignAdGroupsByCriteria({
                dspAdGroupIds: dspAdGroupIds,
            });

            return data;
        },
    });

    const dspAdGroupMap = useMemo(
        (): Record<string, CampaignAdGroup> =>
            adGroups.reduce<Record<string, CampaignAdGroup>>(
                (acc, ag) => ({
                    ...acc,
                    [ag.dspAdGroupId as string]: ag,
                }),
                {}
            ),
        [adGroups]
    );

    const enrichedRows = useMemo(
        () =>
            rows.map((row) => ({
                ...row,
                dspCreativeAssociations: (row.dspCreativeAssociations ?? []).map((assoc) => ({
                    ...assoc,
                    dspAdGroup: dspAdGroupMap[assoc.dspAdGroupId] ?? undefined,
                })),
            })),
        [rows, dspAdGroupMap]
    );

    const columns: GridColDef[] = [
        {
            flex: 1,
            field: 'name',
            headerName: 'Creative Name',
        },
        {
            flex: 1,
            filterable: false,
            field: 'creativeRotationType',
            headerName: 'Creative Type',
            valueGetter: (params, row: DspAdCreative) => {
                return row?.dspCreativeAssociations?.[0]?.dspAdGroup?.creativeRotationType;
            },
            renderCell: ({ value }: GridRenderCellParams) => {
                return <>{value || ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'format',
            headerName: 'Format',
            valueGetter: (params, row: DspAdCreative) => {
                return row?.assets?.[0]?.assetSubTypes;
            },
            renderCell: ({ value }: GridRenderCellParams) => {
                return <>{value || ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'mediaType',
            headerName: 'Media Type',
            valueGetter: (params, row: DspAdCreative) => {
                return row?.dspCreativeAssociations?.[0]?.dspAdGroup?.mediaType;
            },
            renderCell: ({ value }: GridRenderCellParams) => {
                return <CampaignMediaType value={value || campaign.mediaType} />;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'dspAdGroupName',
            headerName: 'Ad Group',
            valueGetter: (params, row: DspAdCreative) => {
                return row?.dspCreativeAssociations?.[0]?.dspAdGroup?.name;
            },
            renderCell: ({ value }: GridRenderCellParams) => {
                return <>{value || ' - '}</>;
            },
        },
        {
            flex: 1,
            filterable: false,
            field: 'action',
            headerName: 'Action',
            headerAlign: 'right',
            align: 'right',
            renderCell: ({ row }: GridRenderCellParams) => {
                return (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                            action.callback(row);
                        }}
                    >
                        {action.name}
                    </Button>
                );
            },
        },
    ];

    const getRowId = (row: DspAdCreative): string | number => {
        return row.dspAdCreativeId as string;
    };

    return (
        <DataGridPro
            // Overridable props
            {...props}
            loading={props?.loading && isLoading}
            disableColumnPinning
            getRowId={getRowId}
            columns={columns}
            rows={enrichedRows}
            slots={{
                toolbar: null,
                noRowsOverlay: NoRowsOverlay,
            }}
            sx={{
                '--DataGrid-overlayHeight': '212px',
            }}
        />
    );
};

export type { CampaignCreativeAction };
export type { CampaignCreativeDataGridProps };
export { NoRowsOverlay as CampaignCreativeDataGridNoRowsOverlay };
export default CampaignCreativeDataGrid;
